// @ts-check
// Disable TypeScript checking for the entire file since we're in a complex JS project
// with component patterns that are difficult to fully type with JSDoc
// @ts-nocheck

import { useState, useCallback, memo } from 'react';
import { Autocomplete, Chip, Icon, InputAdornment, ListItem, TextField } from '@mui/material';
import { useField } from 'formik';

import HelperText from 'Lib/muiComponents/form/HelperText';

/**
 * @typedef {Object} SelectOption
 * @property {string|number} value - The option value
 * @property {string} label - The display label for the option
 * @property {string} [groupLabel] - Optional group label for grouped options
 */

/**
 * @typedef {Object} GroupedOption
 * @property {string} [groupLabel] - Group label
 * @property {SelectOption[]} [options] - Options in this group
 */

/**
 * @typedef {Object} SelectFieldProps
 * @property {string} name - Field name for formik
 * @property {string} [label] - Input label
 * @property {boolean} [required] - Whether the field is required
 * @property {string} [placeholder] - Input placeholder
 * @property {string} [helperText] - Helper text to display
 * @property {boolean} [grouped] - Whether options are grouped
 * @property {Function} [onChange] - Custom change handler
 * @property {Function} [onBlur] - Custom blur handler
 * @property {(SelectOption[]|GroupedOption[])} options - The select options
 * @property {number} [maxLength] - Maximum input length
 * @property {boolean} [disabled] - Whether the field is disabled
 * @property {'dense'|'normal'|'none'} [margin] - Margin size
 * @property {boolean} [hideInputLabel] - Whether to hide the input label
 * @property {string} [startAdornment] - Icon name for start adornment
 * @property {SelectOption|SelectOption[]} [value] - Controlled value
 * @property {boolean} [multiple] - Whether multiple selection is allowed
 */

/**
 * Custom hook to handle select field options
 * @param {Array} options - Options array
 * @param {boolean} grouped - Whether options are grouped
 * @returns {Array} Processed options
 */
const useSelectOptions = (options = [], grouped = false) => {
	if (!grouped || !options?.length) {
		return options || [];
	}

	return options.flatMap(item => (item.options ? item.options : item));
};

/**
 * Multiple select field component
 */
const MultipleSelectField = memo(
	({ name, label, required, placeholder, helperText, options = [], grouped = false, onChange, onBlur, maxLength, disabled, margin = 'normal', value, ...rest }) => {
		const [field, meta, helper] = useField(name);
		const [open, setOpen] = useState(false);
		const groupOptions = useSelectOptions(options, grouped);

		const handleChange = useCallback(
			(__, option) => {
				if (onChange) onChange(option, field, helper);
				helper.setValue(option ? option.value : null);
			},
			[onChange, field, helper]
		);

		const handleBlur = useCallback(
			event => {
				if (onBlur) onBlur(event, field, helper);
				helper.setTouched(true);
			},
			[onBlur, field, helper]
		);

		return (
			<Autocomplete
				{...rest}
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				onChange={handleChange}
				onBlur={handleBlur}
				limitTags={1}
				options={groupOptions}
				disabled={disabled}
				defaultValue={value}
				isOptionEqualToValue={(option, value) => option?.value === value?.value}
				variant="outlined"
				renderInput={params => (
					<TextField
						{...params}
						label={label}
						margin={margin}
						autoComplete="off"
						placeholder={placeholder || `Type to search ${label?.toLowerCase() || ''}`}
						required={required}
						error={!!meta.error}
						helperText={<HelperText meta={meta} required={required} helperText={helperText} />}
						inputProps={{
							maxLength: maxLength,
							...params.inputProps,
							'aria-label': label || name
						}}
					/>
				)}
			/>
		);
	}
);
MultipleSelectField.displayName = 'MultipleSelectField';

/**
 * Single select field component
 */
const SingleSelectField = memo(
	({
		name,
		label,
		required,
		placeholder,
		helperText,
		options = [],
		grouped = false,
		onChange,
		onBlur,
		maxLength,
		disabled,
		margin = 'normal',
		hideInputLabel = false,
		startAdornment,
		value,
		...rest
	}) => {
		const [field, meta, helper] = useField(name);
		const [open, setOpen] = useState(false);
		const groupOptions = useSelectOptions(options, grouped);

		const handleChange = useCallback(
			(__, option) => {
				if (onChange) onChange(option, field, helper);
				helper.setValue(option ? option.value : null);
			},
			[onChange, field, helper]
		);

		const handleBlur = useCallback(
			event => {
				if (onBlur) onBlur(event, field, helper);
				helper.setTouched(true);
			},
			[onBlur, field, helper]
		);

		const startAdornmentIcon = startAdornment ? (
			<InputAdornment position="start">
				<Icon>{startAdornment}</Icon>
			</InputAdornment>
		) : null;

		const displayLabel = hideInputLabel ? '' : label;

		const selectedOption = groupOptions.find(option => option.value === field.value || option.value === value) || null;

		return (
			<Autocomplete
				{...rest}
				open={open}
				value={selectedOption}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				onChange={handleChange}
				onBlur={handleBlur}
				limitTags={1}
				options={groupOptions}
				disabled={disabled}
				variant="outlined"
				getLimitTagsText={amount => `+${amount} more`}
				isOptionEqualToValue={(option, value) => option?.value === value?.value}
				groupBy={option => (grouped ? option.groupLabel : null)}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => <Chip key={index} label={option.label} deleteIcon={<Icon>close</Icon>} {...getTagProps({ index })} />)
				}
				renderOption={(optionProps, option) => (
					<ListItem
						{...optionProps}
						key={optionProps.id}
						sx={{
							backgroundColor: 'unset',
							padding: '12px !important'
						}}
						role="option"
						aria-selected={optionProps['aria-selected']}>
						{option.label}
					</ListItem>
				)}
				renderInput={params => (
					<TextField
						{...params}
						label={displayLabel}
						margin={margin}
						autoComplete="off"
						placeholder={placeholder || `Type to search ${displayLabel?.toLowerCase() || ''}`}
						required={required}
						error={!!meta.error}
						helperText={<HelperText meta={meta} required={required} helperText={helperText} />}
						inputProps={{
							maxLength: maxLength,
							...params.inputProps,
							'aria-label': displayLabel || name
						}}
						InputProps={{
							className: 'field',
							...params.InputProps,
							startAdornment: startAdornmentIcon,
							endAdornment: params.InputProps.endAdornment
						}}
					/>
				)}
			/>
		);
	}
);
SingleSelectField.displayName = 'SingleSelectField';

/**
 * SelectField component that handles both single and multiple selection
 * @param {SelectFieldProps} props - Component props
 */
export const SelectField = memo(props => {
	const { multiple, ...otherProps } = props;

	if (multiple) {
		return <MultipleSelectField {...otherProps} multiple />;
	}

	return <SingleSelectField {...otherProps} />;
});
SelectField.displayName = 'SelectField';

// For backward compatibility
export default SelectField;
